import { Web3Provider } from '@ethersproject/providers';
import { getAddress } from '@ethersproject/address';
import { AddressZero, MaxUint256 } from '@ethersproject/constants';
import { IAGLoanPool, IAGLoanStorage, IAGPool, IAGPool__factory, IAGStakePool, IAGStakePoolManager, IAGTick, IAicadeFactory, IAicadePair, IAicadeRouter, IAppTicket, IAppTicket__factory, IContractRegistry, IERC20, IERC20__factory, IMintTo, IMToken, ITokenManager, ITokenRechargePool } from './contracts/es/typescript/typechain';
import { BigNumber, ContractTransaction, providers } from 'ethers';
import { WalletContext } from './wallet';
import SandboxPoster from '@/framework/events/SandboxPoster';
import Addresses from './Address.json';
import { IContractRegistry__factory } from './contracts/es/typescript/typechain';
import { IAGLoanPool__factory } from './contracts/es/typescript/typechain';
import { IAGTick__factory } from './contracts/es/typescript/typechain';
import { IAGStakePool__factory } from './contracts/es/typescript/typechain';
import { IAGStakePoolManager__factory } from './contracts/es/typescript/typechain';
import contractConstants from './contract.constants';
import i18n from 'i18next';
import { IAicadeRouter__factory } from './contracts/es/typescript/typechain';
import _ from 'lodash';
import { IAGLoanStorage__factory } from './contracts/es/typescript/typechain';
import { ITokenRechargePool__factory } from './contracts/es/typescript/typechain';
import { IMToken__factory } from './contracts/es/typescript/typechain';
import { ITokenManager__factory } from './contracts/es/typescript/typechain';
import { IAicadeFactory__factory } from './contracts/es/typescript/typechain';
import { IAicadePair__factory } from './contracts/es/typescript/typechain';
import { IMintTo__factory } from './contracts/es/typescript/typechain';
// account is optional
function getProviderOrSigner(account?: string): Web3Provider | providers.JsonRpcSigner {
    return account ? getSigner(account) : WalletContext.Provider.library;
}

// account is not optional
function getSigner(account: string): providers.JsonRpcSigner {
    return new providers.Web3Provider(WalletContext.Provider).getSigner();
}

export const newFactory = (factory: ContractFactory, address: string, account?: string): any => {
    if (!WalletContext.Provider || !address) {
        return undefined;
    }
    if (!isAddress(address) || address == AddressZero) {
        throw Error(`Invalid 'address' parameter '${address}'.`);
    }

    return factory.connect(address, getProviderOrSigner(account));
};

export function isAddress(value: string): string | false {
    try {
        return getAddress(value);
    } catch {
        return false;
    }
}
export const createSwapPairController = (controllerAddress: string): IAicadePair | undefined => {
    return newFactory(IAicadePair__factory, controllerAddress, WalletContext.Address);
};

export const createPoolController = (controllerAddress: string): IAGPool | undefined => {
    return newFactory(IAGPool__factory, controllerAddress, WalletContext.Address);
};
export const createLoanPoolController = (controllerAddress: string): IAGLoanPool | undefined => {
    return newFactory(IAGLoanPool__factory, controllerAddress, WalletContext.Address);
};
export const createAGTickController = (controllerAddress: string): IAGTick | undefined => {
    return newFactory(IAGTick__factory, controllerAddress, WalletContext.Address);
};
export const createERC20Controller = (controllerAddress: string): IERC20 | undefined => {
    return newFactory(IERC20__factory, controllerAddress, WalletContext.Address);
};
export const createIMToken = (controllerAddress: string): IMToken | undefined => {
    return newFactory(IMToken__factory, controllerAddress, WalletContext.Address);
};
export const createIMintToToken = (controllerAddress: string): IMintTo | undefined => {
    return newFactory(IMintTo__factory, controllerAddress, WalletContext.Address);
};
export const createTokenManager = (controllerAddress: string): ITokenManager | undefined => {
    return newFactory(ITokenManager__factory, controllerAddress, WalletContext.Address);
};
export const createLoanStorageController = (controllerAddress: string): IAGLoanStorage | undefined => {
    return newFactory(IAGLoanStorage__factory, controllerAddress, WalletContext.Address);
};
export const createStakePoolController = (controllerAddress: string): IAGStakePool | undefined => {
    return newFactory(IAGStakePool__factory, controllerAddress, WalletContext.Address);
};
export const createStakePoolManagerController = (controllerAddress: string): IAGStakePoolManager | undefined => {
    return newFactory(IAGStakePoolManager__factory, controllerAddress, WalletContext.Address);
};
export const createUniswapV2RouterController = (controllerAddress: string): IAicadeRouter | undefined => {
    return newFactory(IAicadeRouter__factory, controllerAddress, WalletContext.Address);
};
export const createUniswapV2FactoryController = (controllerAddress: string): IAicadeFactory | undefined => {
    return newFactory(IAicadeFactory__factory, controllerAddress, WalletContext.Address);
};
export const createAppTicket = (controllerAddress: string): IAppTicket | undefined => {
    return newFactory(IAppTicket__factory, controllerAddress, WalletContext.Address);
};

export const createTokenRechargePool = (controllerAddress: string): ITokenRechargePool | undefined => {
    return newFactory(ITokenRechargePool__factory, controllerAddress, WalletContext.Address);
};

export const createPaymentTokenERC20Controller = async (): Promise<IERC20> => {
    return createERC20Controller(await addressOfByRegistry(contractConstants.Registries.PoolPaymentToken)) as IERC20;
};

// export const createPaymentTokenERC20Controller = (controllerAddress: string): IERC20 | undefined => {
//     return newFactory(IERC20__factory, controllerAddress, WalletContext.Address);
// };
/**
 * 检查拥有者是否授权了特定代币
 * @param token 代币
 * @param owner 拥有者
 * @param spender 消费者
 * @param amount 金额，默认是最大
 * @returns 结果
 */
export const isApproveToken = async (tokenAddress: string, spender: string, amount: BigNumber): Promise<boolean> => {
    if (!tokenAddress || !isAddress(spender) || spender == AddressZero || amount.lte(BigNumber.from('0'))) {
        return false;
    } else {
        const token = createERC20Controller(tokenAddress);
        if (!token) {
            return false;
        }
        const balance = await token.allowance(WalletContext.Address, spender);
        return amount.lte(balance);
    }
};

/**
 * 授权代币消费
 * @param token 消费的代币
 * @param owner 代币拥有者
 * @param spender 代理消费者
 * @param amount 金额
 * @returns 结果
 */
export const approveToken = async (tokenAddress: string, spender: string, amount = MaxUint256): Promise<TransactionData> => {
    return new Promise<TransactionData>((res, rej) => {
        if (!tokenAddress || !isAddress(spender) || spender == AddressZero || amount.lte(BigNumber.from('0'))) {
            rej();
            return;
        } else {
            const token = createERC20Controller(tokenAddress);
            if (!token) {
                rej();
                return;
            }
            token
                .approve(spender, amount)
                .catch(rej)
                .then((o) => res({ transaction: o, type: 'Sign', data: { tokenAddress, spender } }));
        }
    });
};

export const loadingWrapper = (fn: () => Promise<void>, loadText: string): Promise<void> => {
    return new Promise((res, rej) => {
        // 开启事件
        SandboxPoster.Post('Wallet.Signing', loadText);
        fn()
            .then((o) => {
                res(o);
                SandboxPoster.Post('Wallet.SignSuccess', loadText);
            })
            .catch((e) => {
                let msg = i18n.t('error.contract-exe', { ns: 'common' });
                if (e && e.code && e.code == 'ACTION_REJECTED') {
                    msg = i18n.t('wallet.user-cancel', { ns: 'common' });
                }
                rej(e);
                SandboxPoster.Post('Wallet.SignFail', msg);
            });
    });
};

export const signWalletWrapper = (signFn: () => Promise<ContractTransaction>): Promise<ContractTransaction> => {
    return new Promise((res, rej) => {
        signFn()
            .then((o) => {
                res(o);
                SandboxPoster.Post('Wallet.SignSuccess', '交易成功');
            })
            .catch((e) => {
                // console.error(e);
                let msg = i18n.t('error.contract-exe', { ns: 'common' });
                if (e && e.code && e.code == 'ACTION_REJECTED') {
                    msg = i18n.t('wallet.user-cancel', { ns: 'common' });
                }
                rej(e);
                SandboxPoster.Post('Wallet.SignFail', msg);
            });
    });
};

/**
 * 获取合约地址列表
 * @param names 合约名称
 * @returns  结果
 */
export const getContractAddresses = (names: string[]): string[] => {
    if (!WalletContext.Chain) {
        return [];
    }
    const networkName = WalletContext.Chain.name;
    if (!networkName) {
        return [];
    }
    const networkAddresses = (Addresses as any)[networkName];
    const list = [] as string[];
    names.forEach((o) => {
        if (!networkAddresses[o]) {
            return;
        }
        list.push(networkAddresses[o].address as string);
    });
    return list;
};

export const addressOfByRegistry = async (name: string): Promise<string> => {
    const [registryAddress] = getContractAddresses([contractConstants.Names.ContractRegistry]);
    if (_.isEmpty(registryAddress)) {
        return '';
    }
    const registry: IContractRegistry = newFactory(IContractRegistry__factory, registryAddress, WalletContext.Address);
    return registry.addressOf(name);
};

export const balanceOf = async (tokenAddres: string): Promise<BigNumber> => {
    const token = createERC20Controller(tokenAddres);
    if (!token) {
        throw new Error('Network Error.');
    }
    return await token?.balanceOf(WalletContext.Address);
};
